import * as Headless from '@headlessui/react';
import { useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import React from 'react';

import { Avatar } from '@/common/components/avatar';
import { Dropdown, DropdownButton } from '@/common/components/dropdown';
import { Navbar, NavbarItem } from '@/common/components/navbar';
import { useSidebar } from '@/common/components/sidebar';
import {
  Sidebar, SidebarBody,
  SidebarDivider, SidebarFooter, SidebarHeader, SidebarItem, SidebarLabel, SidebarSection, SidebarSpacer
} from '@/common/components/sidebar/sidebar';
import { SidebarLayout } from '@/common/components/sidebar/sidebar-layout';
import { HourglassIcon, InformationCircleIcon, RocketIcon, SidebarIcon, UserSmileIcon } from '@/icons/app';
import { CirclePlusIcon } from '@/icons/fa/light';
import { ChartLineIcon, ChevronDownIcon } from '@/icons/fa/regular';
import { LogoMark } from '@/shared/components/logo-mark';
import { RequireAuth } from '@/shared/components/require-auth';
import { SidebarUserMenu } from '@/shared/components/user-menu';

/**
 * A layout component for the dashboard that includes a navigation sidebar, top navbar, and
 * handles the rendering of children components within the layout structure.
 */
export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const location = useRouterState({ select: state => state.location });

  const isCurrent = (path: string) => location.pathname === path;

  return (
    <SidebarLayout
      navbar={(
        <Navbar>
          <SidebarToggle />
        </Navbar>
      )}
      sidebar={(
        <RequireAuth>
          <Sidebar>
            <AppSidebarHeader />
            <SidebarBody>
              <Dropdown>
                <DropdownButton as={SidebarItem} className="mb-2">
                  <Avatar src="/logo.svg" />
                  <SidebarLabel>Slash Pine Tech</SidebarLabel>
                  <ChevronDownIcon />
                </DropdownButton>
              </Dropdown>
              <SidebarDivider />
              <SidebarSection>
                <SidebarItem current={isCurrent('/time')} href="/time">
                  <HourglassIcon />
                  <SidebarLabel>Time Entry</SidebarLabel>
                </SidebarItem>
                <SidebarItem current={isCurrent('/projects')} href="/projects">
                  <RocketIcon />
                  <SidebarLabel>Projects</SidebarLabel>
                  <AddButton />
                </SidebarItem>
                <SidebarItem current={isCurrent('/clients')} href="/clients">
                  <UserSmileIcon />
                  <SidebarLabel>Clients</SidebarLabel>
                  <AddButton />
                </SidebarItem>
                <SidebarItem current={isCurrent('/reports')} href="/">
                  <ChartLineIcon />
                  <SidebarLabel>Reports</SidebarLabel>
                </SidebarItem>
              </SidebarSection>
              <SidebarSpacer />
              <SidebarSection className="mb-1">
                <SidebarItem>
                  <InformationCircleIcon />
                  <SidebarLabel>Support Center</SidebarLabel>
                </SidebarItem>
              </SidebarSection>
            </SidebarBody>
            <SidebarFooter>
              <SidebarUserMenu />
            </SidebarFooter>
          </Sidebar>
        </RequireAuth>
      )}
    >
      {children}
    </SidebarLayout>
  );
}

function AppSidebarHeader() {
  return (
    <SidebarHeader>
      <div className="flex w-full items-center gap-1">
        <LogoMark className="size-6 m-2 shrink-0" />
        BetterTime
      </div>
    </SidebarHeader>
  );
}

export function AddButton() {
  const { isOpen, isMobile } = useSidebar();

  return (
    <div className="absolute right-2 top-2">
      <Headless.Button
        className={clsx(
          'group rounded-full',
          (isOpen || isMobile) ? 'opacity-100 visible' : 'opacity-0 hidden'
        )}
        onClick={(event) => { event.preventDefault(); }}
      >
        <CirclePlusIcon className="group-data-hover:text-zinc-50 size-6 text-zinc-600" />
      </Headless.Button>
    </div>
  );
}

export function SidebarToggle() {
  const { toggle } = useSidebar();

  return (
    <NavbarItem onClick={toggle}>
      <SidebarIcon />
    </NavbarItem>
  );
}
